/**
 * Timeline utilisateur (support) — C5.
 *
 * Reconstituer le parcours d'un utilisateur demandait ~25 requêtes SQL et des
 * recoupements à la main entre sa session invitée et son compte (enquête ASL
 * JONCOURS). Cette page affiche la même chose en une frise, texte intégral des
 * échanges IA compris.
 *
 * Page d'administration interne : volontairement non traduite, comme le reste
 * de `/admin` (Pulse, Users, Analytics).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Sparkles,
  ArrowLeft,
  AlertTriangle,
  Copy,
  Check,
  Search,
} from "lucide-react";
import {
  pulseMetricsService,
  type AdminTimelineEntry,
} from "@core/services/PulseMetricsService";
import {
  detectAnomalies,
  buildScenario,
} from "@core/lib/admin/timelineAnomalies";

const SOURCES: { key: string; label: string; color: string }[] = [
  { key: "ai", label: "Chat IA", color: "bg-purple-500/20 text-purple-300" },
  {
    key: "ai_event",
    label: "Télémétrie IA",
    color: "bg-fuchsia-500/20 text-fuchsia-300",
  },
  { key: "poll", label: "Sondages", color: "bg-blue-500/20 text-blue-300" },
  {
    key: "invitation",
    label: "Invitations",
    color: "bg-amber-500/20 text-amber-300",
  },
  { key: "vote", label: "Votes émis", color: "bg-green-500/20 text-green-300" },
  {
    key: "vote_received",
    label: "Votes reçus",
    color: "bg-emerald-500/20 text-emerald-300",
  },
  { key: "auth", label: "Connexions", color: "bg-sky-500/20 text-sky-300" },
  { key: "quota", label: "Quotas", color: "bg-orange-500/20 text-orange-300" },
  { key: "email", label: "Emails", color: "bg-cyan-500/20 text-cyan-300" },
  { key: "support", label: "Support", color: "bg-rose-500/20 text-rose-300" },
];

const sourceStyle = (source: string): string =>
  SOURCES.find((s) => s.key === source)?.color ??
  "bg-gray-500/20 text-gray-300";

const dayKey = (iso: string): string =>
  new Date(iso).toLocaleDateString("fr-FR");

const timeLabel = (iso: string): string =>
  new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const str = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

export const UserTimeline: React.FC = () => {
  const { userId = "" } = useParams<{ userId: string }>();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  const [entries, setEntries] = useState<AdminTimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [active, setActive] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      setEntries(await pulseMetricsService.getUserTimeline(userId, days, 500));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }, [userId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (active.size > 0 && !active.has(e.source)) return false;
      if (!needle) return true;
      return (
        e.summary?.toLowerCase().includes(needle) ||
        e.kind?.toLowerCase().includes(needle) ||
        JSON.stringify(e.detail ?? {})
          .toLowerCase()
          .includes(needle)
      );
    });
  }, [entries, active, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, AdminTimelineEntry[]>();
    visible.forEach((e) => {
      const key = dayKey(e.ts);
      map.set(key, [...(map.get(key) ?? []), e]);
    });
    return Array.from(map.entries());
  }, [visible]);

  const anomalies = useMemo(() => detectAnomalies(entries), [entries]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    entries.forEach((e) => map.set(e.source, (map.get(e.source) ?? 0) + 1));
    return map;
  }, [entries]);

  const copyScenario = async () => {
    const scenario = buildScenario(entries);
    // Cas ASL JONCOURS : sessions IA antérieures à la persistance invitée,
    // aucun message stocké — sans ce garde, le bouton copiait une chaîne vide
    // en affichant « copié ».
    if (!scenario) {
      setError("Aucun message utilisateur stocké à copier sur cette période.");
      return;
    }
    try {
      await navigator.clipboard.writeText(scenario);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Copie impossible (presse-papiers indisponible)");
    }
  };

  const toggleSource = (key: string) => {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
      <header className="relative z-10 border-b border-white/5 bg-black/20 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="text-md font-semibold">DooDates Admin</span>
            </Link>
            <Link
              to="/admin/users"
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Utilisateurs
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold break-all">
            {email ?? "Utilisateur"}
          </h1>
          <p className="text-xs text-gray-500 font-mono break-all">{userId}</p>
          <p className="text-sm text-gray-400 mt-1">
            {entries.length} événement{entries.length > 1 ? "s" : ""} sur {days}{" "}
            jours — session invitée comprise
          </p>
        </div>

        {anomalies.length > 0 && (
          <div
            className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg"
            data-testid="timeline-anomalies"
          >
            <div className="flex items-center gap-2 text-amber-300 text-sm font-medium mb-2">
              <AlertTriangle className="w-4 h-4" /> Signaux à vérifier
            </div>
            <ul className="text-xs text-amber-200/90 space-y-1 list-disc list-inside">
              {anomalies.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-2 items-center">
          {SOURCES.filter((s) => (counts.get(s.key) ?? 0) > 0).map((s) => (
            <button
              key={s.key}
              onClick={() => toggleSource(s.key)}
              className={`text-xs px-2 py-1 rounded border transition ${
                active.size === 0 || active.has(s.key)
                  ? `${s.color} border-white/20`
                  : "bg-white/5 text-gray-500 border-white/10"
              }`}
            >
              {s.label} ({counts.get(s.key)})
            </button>
          ))}
        </div>

        <div className="mb-6 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher dans les messages…"
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm"
            />
          </div>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300"
          >
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
            <option value={365}>1 an</option>
          </select>
          <button
            onClick={copyScenario}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-300 hover:bg-white/10 flex items-center gap-2"
            title="Copier les messages utilisateur, dans l'ordre"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-400" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            Copier le scénario
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-sm text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-center text-gray-500 py-12">Chargement…</p>
        ) : grouped.length === 0 ? (
          <p className="text-center text-gray-500 py-12">
            Aucun événement sur cette période.
          </p>
        ) : (
          <div className="space-y-8" data-testid="timeline-entries">
            {grouped.map(([day, dayEntries]) => (
              <section key={day}>
                <h2 className="text-xs uppercase tracking-wide text-gray-500 mb-3">
                  {day}
                </h2>
                <ul className="space-y-2 border-l border-white/10 pl-4">
                  {dayEntries.map((e, i) => (
                    <li
                      key={`${e.ts}-${e.source}-${i}`}
                      className="bg-white/5 border border-white/10 rounded-lg p-3"
                    >
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="font-mono text-gray-500">
                          {timeLabel(e.ts)}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded ${sourceStyle(e.source)}`}
                        >
                          {e.source}
                        </span>
                        <span className="text-gray-400">{e.kind}</span>
                        {e.detail?.guest === true && (
                          <span className="px-2 py-0.5 rounded bg-white/10 text-gray-300">
                            invité
                          </span>
                        )}
                        {e.poll_id && (
                          <Link
                            to={`/poll/${e.poll_id}/results`}
                            className="text-blue-400 hover:underline"
                          >
                            sondage
                          </Link>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-gray-200 whitespace-pre-wrap break-words">
                        {e.source === "ai"
                          ? (str(e.detail?.content) ?? e.summary)
                          : e.summary}
                      </p>
                      {e.detail && Object.keys(e.detail).length > 0 && (
                        <details className="mt-1">
                          <summary className="text-[11px] text-gray-500 cursor-pointer">
                            détail
                          </summary>
                          <pre className="mt-1 text-[11px] text-gray-400 overflow-x-auto whitespace-pre-wrap break-words">
                            {JSON.stringify(e.detail, null, 2)}
                          </pre>
                        </details>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default UserTimeline;
